// src/pages/clab-biodata-rejected/index.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeaderBar from "components/ui/HeaderBar";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import StatusIndicatorBadge from "components/ui/StatusIndicatorBadge";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import { biodataAPI } from "utils/api";
import { useAuth } from "context/AuthContext";
import { getUserRoleForComponents } from "utils/authHelpers";
import { useBiodataManagement } from "hooks/useBiodataManagement";
import { useNotifications } from "hooks/useNotifications";
import { hasEditPermission, getEditPermissionDate } from "utils/biodataHelpers";
import BiodataDetailsView from "components/BiodataDetailsView";
import FilterPanel from "./components/FilterPanel";
import LoadingSpinner from "components/ui/LoadingSpinner";
import ErrorDisplay from "components/ui/ErrorDisplay";
import Notifier from "components/ui/Notifier";
import PaginationComponent from "components/PaginationComponent";
import { useBiodataCache } from "context/BiodataCacheContext";
import RejectionAnalyticsModal from './components/RejectionAnalyticsModal';

const ClabBiodataRejected = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const userRole = getUserRoleForComponents(user);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [grantingPermission, setGrantingPermission] = useState(false);
  const [editPermissionData, setEditPermissionData] = useState({
    reason: "",
    expires_at: "",
  });
  const [bulkActionType, setBulkActionType] = useState("");
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  // Add local state for the modal
  const [showEditPermissionModal, setShowEditPermissionModal] = useState(false);
  // Add local state for details modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  // Add local state for selected biodata details
  const [selectedBiodataDetails, setSelectedBiodataDetails] = useState(null);
  const [exportScope, setExportScope] = useState("all");
  const { getBiodataByStatus, invalidateOnUpdate, getPhotoUrl } =
    useBiodataCache();
  const [biodataList, setBiodataList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Initialize biodata management BEFORE effects that depend on its values
  const biodataManagement = useBiodataManagement({
    apiEndpoints: {
      list: "/biodatas/status/rejected",
      approve: "/clab/biodatas/{id}/approve",
      reject: "/clab/biodatas/{id}/reject",
      grantPermission: "/clab/biodatas/{id}/grant-edit-permission",
      export: "/biodatas/export",
      print: "/biodatas/print",
    },
    initialFilters: {
      search: "",
      nationality: "",
      recruiter_id: "",
      date_from: "",
      date_to: "",
      rejection_reason: "",
      vdr_type: "",
      edit_permission_status: "",
      status_filter: "",
      sort_by: "created_at",
      skill: "",
    },
    pageSize: 10,
    biodataType: "rejected",
    enableDataFetching: false,
  });

  const {
    filters,
    updateFilter,
    resetFilters,
    selectedItems: selectedBiodata,
    selectItem: handleSelectBiodata,
    selectAll: handleSelectAll,
    clearSelection,
    hasSelection,
    selectedCount,
    showBulkActions,
    setShowBulkActions,
    bulkLoading,
    selectedEditData,
    setSelectedEditData,
    goToPage: handlePageChange,
    changePageSize: setItemsPerPage,
    itemsPerPage,
    approveBiodata,
    rejectBiodata,
    grantEditPermission,
    exportBiodata,
    exportStatus,
    printBiodata,
    refetch,
    analytics,
  } = biodataManagement;

  useEffect(() => {
    const fetchRejectedBiodata = async () => {
      setLoading(true);
      try {
        const result = await getBiodataByStatus("rejected", {
          page: currentPage,
          per_page: itemsPerPage,
          ...filters,
        });
        if (result.success) {
          setBiodataList(result.records);
          setPagination({
            current_page: (result.pagination && result.pagination.current_page) || currentPage,
            per_page: (result.pagination && result.pagination.per_page) || itemsPerPage,
            total: (result.pagination && result.pagination.total) || 0,
            total_pages: (result.pagination && result.pagination.total_pages) || ((result.pagination && result.pagination.total && result.pagination.per_page) ? Math.ceil(result.pagination.total / result.pagination.per_page) : 0)
          });
          if (result.fromCache) {
          }
        } else {
          setError(result.message);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRejectedBiodata();
  }, [currentPage, itemsPerPage, filters]);

  // Notifications for user feedback
  const {
    notifications,
    showSuccess,
    showError,
    removeNotification: dismissNotification,
    clearNotifications: clearAllNotifications,
  } = useNotifications();

  // Redirect if not CLAB user - MUST be called before any conditional logic
  useEffect(() => {
    if (
      userProfile &&
      !["clab_user", "clab", "admin", "master_admin", "1", 1].includes(
        userProfile.role
      )
    ) {
      navigate("/login-screen");
    }
  }, [userProfile?.role, navigate]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorDisplay message={error} onRetry={refetch} />;
  }

  // Derived values from custom hook
  const paginatedBiodata = biodataList;

  // Filter change handler
  const handleFilterChange = (key, value) => {
    updateFilter(key, value);
  };

  const handleViewDetails = async (biodata) => {
    try {
      const result = await biodataAPI.getBiodataById(biodata.id);
      if (result?.status === "success" && result.data) {
        setSelectedBiodataDetails(result.data);
        setShowDetailsModal(true);
      } else if (result?.data) {
        setSelectedBiodataDetails(result.data);
        setShowDetailsModal(true);
      } else {
        throw new Error(result?.message || "Failed to load biodata details");
      }
    } catch (error) {
      console.error("Error loading biodata details:", error);
      showError("Failed to load biodata details");
    }
  };

  const handlePrint = (biodata) => {
    // Create a new window for printing
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Rejected Biodata - ${biodata.full_name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .section { margin-bottom: 20px; }
            .label { font-weight: bold; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .rejection-box { background-color: #fee; border: 2px solid #faa; padding: 10px; margin: 20px 0; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Worker Biodata - REJECTED</h1>
            <p>eBiodata CLAB System - Rejected Worker</p>
          </div>
          <div class="rejection-box">
            <h3>REJECTION DETAILS</h3>
            <p><strong>Rejection Reason:</strong> ${
              biodata.latest_rejection?.rejection_reason ||
              "No reason specified"
            }</p>
            <p><strong>Rejected On:</strong> ${
              biodata.latest_rejection?.rejected_at
                ? new Date(
                    biodata.latest_rejection.rejected_at
                  ).toLocaleDateString()
                : "N/A"
            }</p>
            <p><strong>Rejected By:</strong> ${
              biodata.latest_rejection?.rejected_by || "N/A"
            }</p>
            <p><strong>Remarks:</strong> ${
              biodata.latest_rejection?.remarks || "No remarks provided"
            }</p>
          </div>
          <table>
            <tr><th>Worker ID</th><td>${biodata.worker_id}</td></tr>
            <tr><th>Full Name</th><td>${biodata.full_name}</td></tr>
            <tr><th>Nationality</th><td>${biodata.nationality}</td></tr>
            <tr><th>Passport Number</th><td>${biodata.passport_number}</td></tr>
            <tr><th>Date of Birth</th><td>${
              biodata.date_of_birth
                ? new Date(biodata.date_of_birth).toLocaleDateString()
                : "N/A"
            }</td></tr>
            <tr><th>Gender</th><td>${biodata.gender || "N/A"}</td></tr>
            <tr><th>Experience</th><td>${
              biodata.experience_years || 0
            } years</td></tr>
            <tr><th>Skills</th><td>${
              Array.isArray(biodata.skills)
                ? biodata.skills.join(", ")
                : biodata.skills || "N/A"
            }</td></tr>
            <tr><th>Education</th><td>${
              biodata.education_level || "N/A"
            }</td></tr>
            <tr><th>Medical Status</th><td>${
              biodata.medical_status || "N/A"
            }</td></tr>
            <tr><th>Languages</th><td>${
              Array.isArray(biodata.language_skills)
                ? biodata.language_skills.join(", ")
                : "N/A"
            }</td></tr>
            <tr><th>Submission Date</th><td>${
              biodata.submission_date
                ? new Date(biodata.submission_date).toLocaleDateString()
                : "N/A"
            }</td></tr>
            <tr><th>Status</th><td>REJECTED</td></tr>
          </table>
          <div class="section">
            <p><strong>Recruiter:</strong> ${
              biodata.recruiters?.agency_name || "Unknown"
            }</p>
            <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleApprove = async (biodataId, notes = "Approved by CLAB user") => {
    const result = await approveBiodata(biodataId, notes);
    if (result.success) {
      showSuccess(result.message);
    } else {
      showError(result.message);
    }
  };

  const handleReject = async (biodataIds, rejectionReason) => {
    const ids = Array.isArray(biodataIds) ? biodataIds : [biodataIds];
    const result = await rejectBiodata(ids[0], rejectionReason.reason_id);
    if (result.success) {
      showSuccess(result.message);
    } else {
      showError(result.message);
    }
  };

  const handleEditPermissionGranted = (biodataId) => {
    setShowDetailsModal(false);
    setSelectedBiodataDetails(null);
    setShowEditPermissionModal(false);
    setSelectedEditData(null);
    setEditPermissionData({ reason: "", expires_at: "" });
    refetch(); // Refresh the data
  };

  const handleGrantEditPermissionFromTable = (biodata) => {
    setSelectedEditData(biodata);
    setShowEditPermissionModal(true);
  };

  // Helper functions are now imported from utils/biodataHelpers

  const handleGrantEditPermission = async () => {
    if (!editPermissionData.reason.trim()) {
      showError("Please provide a reason for granting edit permission");
      return;
    }

    try {
      setGrantingPermission(true);
      const result = await grantEditPermission(
        selectedEditData.id,
        editPermissionData.reason,
        editPermissionData.expires_at
      );
      invalidateOnUpdate([selectedEditData.id], "rejected", "resubmitted");
      if (result.success) {
        handleEditPermissionGranted(selectedEditData.id);
        showSuccess(
          "Edit permission granted successfully. The recruiter will be notified."
        );
      } else {
        showError(result.message);
      }
    } catch (error) {
      console.error("Failed to grant edit permission:", error);
      showError("Failed to grant edit permission. Please try again.");
    } finally {
      setGrantingPermission(false);
    }
  };

  // Bulk Operations Functions - now handled by custom hooks
  // handleSelectAll, handleSelectBiodata, and clearSelection are provided by useBulkActions

  const handleBulkEditPermission = async () => {
    if (!editPermissionData.reason.trim()) {
      showError("Please provide a reason for granting edit permission");
      return;
    }

    const result = await biodataManagement.handleBulkGrantPermission();
    if (result.success) {
      setShowEditPermissionModal(false);
      setEditPermissionData({ reason: "", expires_at: "" });
      showSuccess(result.message);
    } else {
      showError(result.message);
    }
  };

  const handleBulkPrint = () => {
    const result = printBiodata(selectedBiodata);
    if (result.success) {
      showSuccess(result.message);
    } else {
      showError(result.message);
    }
  };

  const handleBulkExport = async () => {
    try {
      if (
        exportScope === "current_page" &&
        (!biodataList || biodataList.length === 0)
      ) {
        showError("No rejected biodata on the current page to export");
        return;
      }

      const exportFilters = exportScope === "filtered" ? filters : undefined;
      const pagination =
        exportScope === "current_page"
          ? { page: currentPage, perPage: itemsPerPage }
          : undefined;

      await exportBiodata([], "excel", {
        statusFilter: exportScope === "all" ? "all" : "rejected",
        includeDocuments: false,
        includePhotos: false,
        useBackend: true,
        exportScope,
        filters: exportFilters,
        pagination,
      });
    } catch (error) {
      console.error("Export error:", error);
      showError(
        "Failed to export rejected biodata: " +
          (error.message || "Unknown error")
      );
    }
  };

  // Analytics loading is now handled by the custom hook

  // Smart notification generation and management is now handled by the custom hook

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderBar
          userRole={userRole}
          userName={userProfile?.full_name || "CLAB User"}
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <RoleBasedSidebar
          userRole={userRole}
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <main
          className={`transition-all duration-300 pt-16 ${
            sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
          }`}
        >
          <div className="p-6 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Icon
                name="Loader2"
                size={48}
                className="animate-spin text-primary mx-auto mb-4"
              />
              <p className="text-text-secondary">Loading rejected biodata...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Biodata Details Overlay using shared viewer */}
      {showDetailsModal && selectedBiodataDetails && (
        <>
          <style>{`
            @media print {
              .biodata-modal-overlay {
                display: none !important;
              }
              .biodata-modal-content {
                position: static !important;
                max-width: 100% !important;
                max-height: none !important;
                box-shadow: none !important;
              }
            }
          `}</style>
          <div className="biodata-modal-overlay fixed inset-0 z-1001 bg-secondary-900 bg-opacity-50 flex items-center justify-center p-4">
            <div className="biodata-modal-content bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-secondary-200 flex items-center justify-between">
                <h2 className="text-xl font-bold">Rejected Biodata Details</h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedBiodataDetails(null);
                  }}
                  className="text-secondary-600 hover:text-secondary-900"
                >
                  Close
                </button>
              </div>
              <div className="p-6">
                <BiodataDetailsView biodata={selectedBiodataDetails} />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Global Notifier for success/error toasts */}
      <Notifier
        type={
          notifications.length > 0
            ? notifications[notifications.length - 1].type
            : "info"
        }
        message={
          notifications.length > 0
            ? notifications[notifications.length - 1].message
            : ""
        }
        show={notifications.length > 0}
        onClose={() =>
          dismissNotification(notifications[notifications.length - 1]?.id)
        }
        duration={
          notifications.length > 0
            ? notifications[notifications.length - 1].duration
            : 3000
        }
      />
      <HeaderBar
        userRole={userRole}
        userName={userProfile?.full_name || "CLAB User"}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <RoleBasedSidebar
        userRole={userRole}
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`transition-all duration-300 pt-16 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-6">
          <BreadcrumbNavigation userRole={userRole} />

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              Rejected Biodata
            </h1>
            <p className="text-text-secondary">
              View rejected foreign worker biodata with rejection reasons
            </p>
          </div>

          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <h3 className="font-semibold text-error-800">Error</h3>
              </div>
              <p className="text-error-700 mt-2">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Filter Panel */}
            <FilterPanel
              filters={filters}
              onFiltersChange={(newFilters) => {
                Object.keys(newFilters).forEach((key) => {
                  updateFilter(key, newFilters[key]);
                });
              }}
            />

            {/* Biodata Table */}
            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div>
                      <h2 className="text-xl font-semibold text-text-primary">
                        Rejected Biodata ({pagination?.total || 0})
                      </h2>
                    </div>
                    {selectedBiodata.length > 0 && (
                      <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                        {selectedBiodata.length} selected
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {selectedBiodata.length > 0 && (
                      <>
                        <button
                          onClick={() => setShowBulkActions(true)}
                          className="flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-md hover:bg-primary-700 transition-colors duration-150"
                        >
                          <Icon name="Settings" size={16} />
                          <span>Bulk Actions</span>
                        </button>
                        <button
                          onClick={clearSelection}
                          className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-md transition-colors duration-150"
                          title="Clear Selection"
                        >
                          <Icon name="X" size={16} />
                        </button>
                      </>
                    )}
                    <div className="relative">
                      <button
                        onClick={() => setShowNotifications(!showNotifications)}
                        className="relative flex items-center space-x-2 p-2 text-secondary-700 hover:bg-secondary-50 rounded-md transition-colors duration-150"
                      >
                        <Icon name="Bell" size={20} />
                        {notifications.length > 0 && (
                          <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-error text-white text-xs font-medium rounded-full">
                            {notifications.length > 9
                              ? "9+"
                              : notifications.length}
                          </span>
                        )}
                      </button>

                      {/* Notification Dropdown */}
                      {showNotifications && (
                        <div className="absolute right-0 mt-2 w-80 bg-surface border border-secondary-200 rounded-lg shadow-xl z-1003">
                          <div className="p-4 border-b border-secondary-200">
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold text-text-primary">
                                Notifications
                              </h3>
                              {notifications.length > 0 && (
                                <button
                                  onClick={clearAllNotifications}
                                  className="text-xs text-secondary-600 hover:text-secondary-800"
                                >
                                  Clear All
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="max-h-96 overflow-y-auto">
                            {notifications.length === 0 ? (
                              <div className="p-6 text-center text-secondary-500">
                                <Icon
                                  name="Bell"
                                  size={32}
                                  className="mx-auto mb-2 opacity-50"
                                />
                                <p>No notifications yet</p>
                              </div>
                            ) : (
                              notifications.map((notification) => (
                                <div
                                  key={notification.id}
                                  className="border-b border-secondary-100 last:border-b-0"
                                >
                                  <div className="p-4 hover:bg-secondary-50">
                                    <div className="flex items-start space-x-3">
                                      <div
                                        className={`p-1 rounded-full ${
                                          notification.type === "success"
                                            ? "bg-success-100"
                                            : notification.type === "warning"
                                            ? "bg-warning-100"
                                            : notification.type === "error"
                                            ? "bg-error-100"
                                            : "bg-accent-100"
                                        }`}
                                      >
                                        <Icon
                                          name={
                                            notification.type === "success"
                                              ? "CheckCircle"
                                              : notification.type === "warning"
                                              ? "AlertTriangle"
                                              : notification.type === "error"
                                              ? "XCircle"
                                              : "Info"
                                          }
                                          size={16}
                                          className={
                                            notification.type === "success"
                                              ? "text-success"
                                              : notification.type === "warning"
                                              ? "text-warning"
                                              : notification.type === "error"
                                              ? "text-error"
                                              : "text-accent"
                                          }
                                        />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h4 className="text-sm font-medium text-text-primary">
                                          {notification.title}
                                        </h4>
                                        <p className="text-xs text-text-secondary mt-1">
                                          {notification.message}
                                        </p>
                                        <p className="text-xs text-secondary-500 mt-2">
                                          {notification.timestamp.toLocaleTimeString()}
                                        </p>
                                        {notification.actionable && (
                                          <button
                                            onClick={() => {
                                              notification.action();
                                              setShowNotifications(false);
                                            }}
                                            className="text-xs text-primary hover:text-primary-700 font-medium mt-2"
                                          >
                                            Take Action →
                                          </button>
                                        )}
                                      </div>
                                      <button
                                        onClick={() =>
                                          dismissNotification(notification.id)
                                        }
                                        className="p-1 text-secondary-400 hover:text-secondary-600"
                                      >
                                        <Icon name="X" size={12} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setShowAnalytics(true)}
                      className="flex items-center space-x-2 px-4 py-2 text-accent bg-accent-50 border border-accent-200 rounded-md hover:bg-accent-100 transition-colors duration-150"
                    >
                      <Icon name="BarChart3" size={16} />
                      <span>Analytics</span>
                    </button>
                    <div className="flex flex-col items-end space-y-2">
                      <div className="flex items-center space-x-2">
                        <select
                          value={exportScope}
                          onChange={(e) => setExportScope(e.target.value)}
                          className="px-3 py-2 text-sm border border-secondary-300 rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-primary-300"
                        >
                          <option value="all">
                            All records (all statuses)
                          </option>
                          <option value="filtered">
                            Apply current filters
                          </option>
                          <option value="current_page">
                            Current page only
                          </option>
                        </select>
                        <button
                          onClick={handleBulkExport}
                          disabled={exportStatus.isExporting}
                          className="flex items-center space-x-2 px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="Download" size={16} />
                          <span>
                            {exportStatus.isExporting
                              ? "Exporting..."
                              : "Export"}
                          </span>
                        </button>
                      </div>
                      {exportStatus.isExporting ||
                      exportStatus.stage === "downloading" ? (
                        <p className="text-xs text-text-secondary">
                          Exporting {exportStatus.totalRecords || "records"}
                          {exportStatus.estimatedSeconds
                            ? ` (~${Math.max(
                                1,
                                Math.round(exportStatus.estimatedSeconds)
                              )}s)`
                            : ""}{" "}
                          {exportStatus.downloadedBytes
                            ? `• ${
                                Math.round(
                                  (exportStatus.downloadedBytes / 1024 / 1024) *
                                    100
                                ) / 100
                              } MB downloaded`
                            : ""}
                        </p>
                      ) : exportStatus.stage === "completed" ? (
                        <p className="text-xs text-success-600">
                          Last export: {exportStatus.totalRecords || 0} records
                          → {exportStatus.filename || "download"}
                        </p>
                      ) : exportStatus.stage === "error" ? (
                        <p className="text-xs text-error-600">
                          Export failed: {exportStatus.error}
                        </p>
                      ) : null}
                    </div>
                    <button
                      onClick={() => window.location.reload()}
                      className="flex items-center space-x-2 px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150"
                    >
                      <Icon name="RefreshCw" size={16} />
                      <span>Refresh</span>
                    </button>
                  </div>
                </div>
              </div>

              {paginatedBiodata.length === 0 ? (
                <div className="p-12 text-center">
                  <Icon
                    name="FileText"
                    size={48}
                    className="text-secondary-300 mx-auto mb-4"
                  />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    No Rejected Biodata
                  </h3>
                  <p className="text-text-secondary">
                    No rejected biodata available.
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary-50 border-b border-secondary-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider w-12">
                            <input
                              type="checkbox"
                              checked={
                                selectedBiodata.length ===
                                  paginatedBiodata.length &&
                                paginatedBiodata.length > 0
                              }
                              onChange={handleSelectAll}
                              className="h-4 w-4 text-primary focus:ring-primary border-secondary-300 rounded"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Worker No
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Passport No
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Nationality
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Registered Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Medical Expiry
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            VDR Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Rejection Date & Reviewer
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Edit Permission
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Rejection Reason
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-surface divide-y divide-secondary-200">
                        {paginatedBiodata.map((biodata) => (
                          <tr
                            key={biodata.id}
                            className="hover:bg-secondary-50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4">
                              <input
                                type="checkbox"
                                checked={selectedBiodata.includes(biodata.id)}
                                onChange={() => handleSelectBiodata(biodata.id)}
                                className="h-4 w-4 text-primary focus:ring-primary border-secondary-300 rounded"
                              />
                            </td>
                            <td className="px-6 py-4 font-mono text-xs">
                              {biodata.worker_id || "-"}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary-100">
                                  <Image
                                    src={getPhotoUrl(biodata.id, biodata)}
                                    alt={biodata.full_name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-text-primary">
                                    {biodata.full_name}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.passport_no ||
                                biodata.passport_number ||
                                "-"}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.nationality || "-"}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.created_at
                                ? new Date(
                                    biodata.created_at
                                  ).toLocaleDateString()
                                : "-"}
                            </td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.medical_expiry_date
                                ? new Date(
                                    biodata.medical_expiry_date
                                  ).toLocaleDateString()
                                : "-"}
                            </td>
                            <td className="px-6 py-4">
                              {biodata.vdr_type && (
                                <span className="inline-flex items-center px-2 py-1 bg-accent-100 text-accent-800 rounded-full text-xs font-medium">
                                  <Icon
                                    name="Briefcase"
                                    size={12}
                                    className="mr-1"
                                  />
                                  {biodata.vdr_type}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-xs text-text-primary">
                                  {biodata.rejected_at
                                    ? new Date(
                                        biodata.rejected_at
                                      ).toLocaleDateString()
                                    : "-"}
                                </p>
                                <p className="text-xs text-text-secondary">
                                  {biodata.reviewed_by_profile?.full_name ||
                                    "-"}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {hasEditPermission(biodata) ? (
                                <div className="flex items-center space-x-2">
                                  <div className="flex items-center space-x-1">
                                    <div className="w-2 h-2 bg-success rounded-full"></div>
                                    <span className="text-xs text-success-700 font-medium">
                                      Granted
                                    </span>
                                  </div>
                                  {getEditPermissionDate(biodata) && (
                                    <div className="text-xs text-text-secondary">
                                      {getEditPermissionDate(
                                        biodata
                                      ).toLocaleDateString()}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center space-x-1">
                                  <div className="w-2 h-2 bg-secondary-300 rounded-full"></div>
                                  <span className="text-xs text-secondary-600">
                                    Not Granted
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="max-w-xs space-y-2">
                                {biodata.rejection_reasons &&
                                biodata.rejection_reasons.length > 0 ? (
                                  <>
                                    <div className="flex flex-wrap gap-1">
                                      {biodata.rejection_reasons.map(
                                        (rejection, idx) => (
                                          <span
                                            key={idx}
                                            className="inline-flex items-center px-2 py-1 bg-error-100 text-error-800 rounded text-xs font-medium"
                                            title={rejection.rejection_reason}
                                          >
                                            {rejection.rejection_reason ||
                                              "No reason"}
                                          </span>
                                        )
                                      )}
                                    </div>
                                    {biodata.latest_rejection?.remarks && (
                                      <p
                                        className="text-xs text-text-secondary truncate"
                                        title={biodata.latest_rejection.remarks}
                                      >
                                        Remarks:{" "}
                                        {biodata.latest_rejection.remarks}
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-sm text-error-700">
                                    No reason provided
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleViewDetails(biodata)}
                                  className="p-2 text-accent hover:bg-accent-50 rounded-md transition-colors duration-150"
                                  title="View Details"
                                >
                                  <Icon name="Eye" size={16} />
                                </button>
                                {hasEditPermission(biodata) ? (
                                  <div className="relative group">
                                    <button
                                      disabled
                                      className="p-2 text-success bg-success-50 rounded-md cursor-not-allowed opacity-75"
                                      title="Edit Permission Already Granted"
                                    >
                                      <Icon name="CheckCircle" size={16} />
                                    </button>
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-secondary-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap z-10">
                                      Edit permission already granted
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() =>
                                      handleGrantEditPermissionFromTable(
                                        biodata
                                      )
                                    }
                                    className="p-2 text-warning hover:bg-warning-50 rounded-md transition-colors duration-150"
                                    title="Grant Edit Permission"
                                  >
                                    <Icon name="Edit" size={16} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handlePrint(biodata)}
                                  className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-md transition-colors duration-150"
                                  title="Print Biodata"
                                >
                                  <Icon name="Printer" size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Component */}
                  {pagination && (
                    <div className="px-6 py-4 border-t border-secondary-200">
                      <PaginationComponent
                        pagination={{
                          currentPage: currentPage,
                          perPage: pagination?.per_page || itemsPerPage,
                          total: pagination?.total || 0,
                          totalPages: pagination?.total_pages || Math.ceil((pagination?.total || 0) / (pagination?.per_page || itemsPerPage)),
                          from: (currentPage - 1) * (pagination?.per_page || itemsPerPage) + 1,
                          to: Math.min(currentPage * (pagination?.per_page || itemsPerPage), pagination?.total || 0),
                        }}
                        onPageChange={setCurrentPage}
                        onPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
                        showPerPageSelector={true}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Biodata Details Modal */}
      {showDetailsModal && selectedBiodataDetails && (
        <>
          <style>{`
            @media print {
              .biodata-modal-overlay {
                display: none !important;
              }
              .biodata-modal-content {
                position: static !important;
                max-width: 100% !important;
                max-height: none !important;
                box-shadow: none !important;
              }
            }
          `}</style>
          <div className="biodata-modal-overlay fixed inset-0 z-1001 bg-secondary-900 bg-opacity-50 flex items-center justify-center p-4">
            <div className="biodata-modal-content bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-secondary-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-text-primary">
                  Rejected Biodata Details
                </h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedBiodataDetails(null);
                  }}
                  className="text-secondary-600 hover:text-secondary-900"
                >
                  Close
                </button>
              </div>
              <div className="p-6">
                <BiodataDetailsView biodata={selectedBiodataDetails} />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Analytics Dashboard Modal */}
      {showAnalytics && (
        <RejectionAnalyticsModal
          analytics={analytics}
          onClose={() => setShowAnalytics(false)}
        />
      )}

      {/* Bulk Actions Modal */}
      {showBulkActions && (
        <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-1002 p-4">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-text-primary">
                Bulk Actions
              </h3>
              <button
                onClick={() => setShowBulkActions(false)}
                className="p-2 text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors duration-150"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Info" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-primary-800">
                    {selectedBiodata.length} biodata selected for bulk action
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    setBulkActionType("edit_permission");
                    setShowBulkActions(false);
                    setShowEditPermissionModal(true);
                  }}
                  className="w-full flex items-center space-x-3 p-3 text-left bg-warning-50 border border-warning-200 rounded-lg hover:bg-warning-100 transition-colors duration-150"
                >
                  <Icon name="Edit" size={20} className="text-warning" />
                  <div>
                    <h4 className="font-medium text-warning-800">
                      Grant Edit Permission
                    </h4>
                    <p className="text-sm text-warning-700">
                      Allow recruiters to edit selected biodata
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => {
                    handleBulkPrint();
                    setShowBulkActions(false);
                  }}
                  className="w-full flex items-center space-x-3 p-3 text-left bg-accent-50 border border-accent-200 rounded-lg hover:bg-accent-100 transition-colors duration-150"
                >
                  <Icon name="Printer" size={20} className="text-accent" />
                  <div>
                    <h4 className="font-medium text-accent-800">Bulk Print</h4>
                    <p className="text-sm text-accent-700">
                      Print all selected biodata in one document
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => {
                    handleBulkExport();
                    setShowBulkActions(false);
                  }}
                  className="w-full flex items-center space-x-3 p-3 text-left bg-success-50 border border-success-200 rounded-lg hover:bg-success-100 transition-colors duration-150"
                >
                  <Icon name="Download" size={20} className="text-success" />
                  <div>
                    <h4 className="font-medium text-success-800">
                      Export to CSV
                    </h4>
                    <p className="text-sm text-success-700">
                      Download selected biodata as CSV file
                    </p>
                  </div>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end p-6 border-t border-secondary-200 bg-secondary-50 space-x-3">
              <button
                onClick={() => setShowBulkActions(false)}
                className="px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Permission Modal */}
      {showEditPermissionModal &&
        (selectedEditData || bulkActionType === "edit_permission") && (
          <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-1002 p-4">
            <div className="bg-surface rounded-lg shadow-xl w-full max-w-md overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-secondary-200">
                <h3 className="text-lg font-semibold text-text-primary">
                  Grant Edit Permission
                </h3>
                <button
                  onClick={() => {
                    setShowEditPermissionModal(false);
                    setSelectedEditData(null);
                    setBulkActionType("");
                    setEditPermissionData({ reason: "", expires_at: "" });
                  }}
                  className="p-2 text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors duration-150"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <Icon
                      name="AlertTriangle"
                      size={20}
                      className="text-warning mt-0.5"
                    />
                    <div>
                      <h4 className="font-medium text-warning-800 mb-1">
                        Grant Edit Permission
                      </h4>
                      <p className="text-sm text-warning-700">
                        {bulkActionType === "edit_permission" ? (
                          <>
                            This will allow recruiters to edit and resubmit{" "}
                            <strong>
                              {selectedBiodata.length} selected rejected biodata
                            </strong>
                            .
                          </>
                        ) : (
                          <>
                            This will allow the recruiter to edit and resubmit
                            the rejected biodata for{" "}
                            <strong>{selectedEditData?.full_name}</strong>.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Reason for Edit Permission{" "}
                    <span className="text-error">*</span>
                  </label>
                  <textarea
                    value={editPermissionData.reason}
                    onChange={(e) =>
                      setEditPermissionData((prev) => ({
                        ...prev,
                        reason: e.target.value,
                      }))
                    }
                    placeholder="Explain why edit permission is being granted (e.g., specific issues to be corrected)"
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Permission Expiry (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={editPermissionData.expires_at}
                    onChange={(e) =>
                      setEditPermissionData((prev) => ({
                        ...prev,
                        expires_at: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                    min={new Date().toISOString().slice(0, 16)}
                  />
                  <p className="text-xs text-text-secondary mt-1">
                    Leave empty for permanent permission or set expiry date
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end p-6 border-t border-secondary-200 bg-secondary-50 space-x-3">
                <button
                  onClick={() => {
                    setShowEditPermissionModal(false);
                    setSelectedEditData(null);
                    setBulkActionType("");
                    setEditPermissionData({ reason: "", expires_at: "" });
                  }}
                  className="px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150"
                  disabled={grantingPermission || bulkLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={
                    bulkActionType === "edit_permission"
                      ? handleBulkEditPermission
                      : handleGrantEditPermission
                  }
                  disabled={
                    grantingPermission ||
                    bulkLoading ||
                    !editPermissionData.reason.trim()
                  }
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {(grantingPermission || bulkLoading) && (
                    <Icon name="Loader" size={16} className="animate-spin" />
                  )}
                  <span>
                    {grantingPermission || bulkLoading
                      ? "Granting..."
                      : bulkActionType === "edit_permission"
                      ? `Grant Permission (${selectedBiodata.length})`
                      : "Grant Permission"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default ClabBiodataRejected;
